import { UserAccountCreationEvent } from '../analytics/models';
import { login, logout } from '../redux/slices/applicationSlice';
import { store } from '../redux/store';
import { InviteType, CustomFieldResponses } from '../types/invites';
import { AfterRegistrationAction, RegisterRequest, Language, Origin } from '../types/requests';
import { ProfileField } from '../types/profile';
import { LoginResponse, RegisterResponse } from '../types/responses';
import httpService, { setRefreshToken, setToken, wipeToken } from './httpService';

export async function loginMagic(magicLinkId: string): Promise<void> {
  const {
    data: { accessToken, refreshToken, userAlias, userEmail, userId, userRole: role },
  } = await httpService.post<LoginResponse>('/auth/login', { magicLinkToken: magicLinkId });

  const [firstName, lastName] = userAlias.split('+');
  setToken(accessToken);
  setRefreshToken(refreshToken);

  // Fetch communities and businesses from the API
  const { data: communities } = await httpService.get('/networks/communities/@me');
  const { data: businesses } = await httpService.get('/networks/businesses');

  store.dispatch(logout());
  store.dispatch(
    login({
      userId,
      firstName,
      lastName,
      email: userEmail,
      alias: userAlias,
      communities,
      businesses,
      role,
    }),
  );
}

export function generateMagicLink(
  email: string,
  invitationId?: string,
  communityInvitationId?: string,
  inviteType?: InviteType,
  personalLinkId?: string,
  eventId?: string,
): Promise<void> {
  return httpService.post(
    '/auth/magic-link',
    { email },
    {
      params: {
        source: Origin.WEB,
        invitationId,
        communityInvitationId,
        personalLinkId,
        eventId,
        inviteType,
      },
    },
  );
}

export function logOut(): void {
  wipeToken();
  store.dispatch(logout());
}

export function register(
  alias: string,
  email: string,
  creationEvent: UserAccountCreationEvent,
  options?: {
    afterAction?: AfterRegistrationAction;
    afterActionData?: Record<string, unknown>;
  },
): Promise<() => void> {
  const [firstName, lastName] = alias.split('+');
  alias = `${firstName.trim()}+${lastName.trim()}`;
  const body: RegisterRequest = {
    alias,
    email,
    creationEvent,
    afterAction: options?.afterAction ?? AfterRegistrationAction.None,
    afterActionData: options?.afterActionData,
  };

  return httpService
    .post<RegisterResponse>('/auth/register', body, { params: { source: Origin.WEB } })
    .then(({ data: { accessToken, refreshToken } }): (() => void) => {
      setToken(accessToken);
      setRefreshToken(refreshToken);

      return () =>
        setTimeout(() => {
          store.dispatch(
            login({
              alias,
              email,
            }),
          );
        }, 1);
    });
}

export function mapInstantRegisterToRegisterOptions(params: {
  invitationId: string;
  inviteType: InviteType;
  data: ProfileField[];
  lang: Language;
  customFieldResponses?: CustomFieldResponses;
}): { afterAction: AfterRegistrationAction; afterActionData: Record<string, unknown> } {
  const { invitationId, inviteType, data, lang, customFieldResponses } = params;
  switch (inviteType) {
    case InviteType.USER:
    case InviteType.PERSONAL:
      return {
        afterAction: AfterRegistrationAction.UseInvitationLink,
        afterActionData: { linkId: invitationId, data },
      };
    case InviteType.COMMUNITY_CONTACT_INVITE:
      return {
        afterAction: AfterRegistrationAction.UseCommunityInvite,
        afterActionData: { inviteId: invitationId, lang, customFieldResponses },
      };
    case InviteType.BUSINESS:
      return {
        afterAction: AfterRegistrationAction.UseBusinessInvite,
        afterActionData: { inviteId: invitationId, lang, customFieldResponses },
      };
    default:
      return {
        afterAction: AfterRegistrationAction.None,
        afterActionData: {},
      };
  }
}
